import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import AuthGuard from '../guards/AuthGuard';
import GuestGuard from '../guards/GuestGuard';
// components
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'login',
      element: (
        <GuestGuard>
          <Login />
        </GuestGuard>
      ),
    },
    { path: '/', element: <Navigate to="/dashboard/app" replace /> },
    {
      path: '/dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/dashboard/app" replace />, index: true },
        { path: 'app', element: <DashboardPage /> },
        { path: 'orders', element: <OrdersPage /> },
        { path: 'menu', element: <MenuPage /> },
        { path: 'packages', element: <PackagesPage /> },
        { path: 'inventory', element: <InventoryPage /> },
        { path: 'forecast', element: <ForecastPage /> },
        { path: 'analytics', element: <AnalyticsPage /> },
        { path: 'promotions', element: <PromotionsPage /> },
        { path: 'slots', element: <SlotsPage /> },
        { path: 'broadcasts', element: <BroadcastsPage /> },
        { path: 'corporate', element: <CorporatePage /> },
        { path: 'corporate/:id', element: <OrganizationPage /> },
        { path: 'employees', element: <EmployeesPage /> },
        { path: 'settings', element: <SettingsPage /> },
      ],
    },
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

const Login = Loadable(lazy(() => import('../pages/Login')));
const DashboardPage = Loadable(lazy(() => import('../pages/Dashboard')));
const OrdersPage = Loadable(lazy(() => import('../pages/Orders')));
const MenuPage = Loadable(lazy(() => import('../pages/Menu')));
const PackagesPage = Loadable(lazy(() => import('../pages/Packages')));
const InventoryPage = Loadable(lazy(() => import('../pages/Inventory')));
const ForecastPage = Loadable(lazy(() => import('../pages/Forecast')));
const AnalyticsPage = Loadable(lazy(() => import('../pages/Analytics')));
const PromotionsPage = Loadable(lazy(() => import('../pages/Promotions')));
const SlotsPage = Loadable(lazy(() => import('../pages/Slots')));
const BroadcastsPage = Loadable(lazy(() => import('../pages/Broadcasts')));
const CorporatePage = Loadable(lazy(() => import('../pages/Corporate')));
const OrganizationPage = Loadable(lazy(() => import('../pages/Organization')));
const EmployeesPage = Loadable(lazy(() => import('../pages/Employees')));
const SettingsPage = Loadable(lazy(() => import('../pages/Settings')));
const NotFound = Loadable(lazy(() => import('../pages/Page404')));
